import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { generatePuzzle, isSolved, getPeerIndexes, idx, } from "../lib";
const emptyNotes = () => Array.from({ length: 81 }, () => new Set());
const initialState = {
    puzzle: null,
    board: new Array(81).fill(0),
    notes: emptyNotes(),
    selectedCell: null,
    status: "idle",
    hintsUsed: 0,
    elapsedSeconds: 0,
    notesMode: false,
    notesPendingCell: null,
    isDaily: false,
    dailyDate: null,
};
// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
function gameReducer(state, action) {
    switch (action.type) {
        case "START_GAME": {
            return {
                ...initialState,
                puzzle: action.puzzle,
                board: [...action.puzzle.puzzle],
                notes: emptyNotes(),
                status: "playing",
                isDaily: false,
                dailyDate: null,
            };
        }
        case "START_DAILY_GAME": {
            return {
                ...initialState,
                puzzle: action.puzzle,
                board: [...action.puzzle.puzzle],
                notes: emptyNotes(),
                status: "playing",
                isDaily: true,
                dailyDate: action.date,
            };
        }
        case "RESTORE_GAME": {
            return {
                ...initialState,
                puzzle: action.puzzle,
                board: action.board,
                notes: action.notes,
                elapsedSeconds: action.timerSeconds,
                hintsUsed: action.hintsUsed,
                status: "playing",
            };
        }
        case "SELECT_CELL": {
            return { ...state, selectedCell: action.index, notesPendingCell: null };
        }
        case "ENTER_VALUE": {
            const { selectedCell, puzzle, board, notes, notesMode } = state;
            if (selectedCell === null || puzzle === null)
                return state;
            if (puzzle.puzzle[selectedCell] !== 0)
                return state;
            if (state.status !== "playing")
                return state;
            const { value } = action;
            const effectiveNotesMode = state.notesPendingCell === selectedCell ? true : notesMode;
            if (effectiveNotesMode) {
                const newNotes = notes.map((s, i) => i === selectedCell ? new Set(s) : s);
                const cellNotes = newNotes[selectedCell];
                if (cellNotes.has(value))
                    cellNotes.delete(value);
                else
                    cellNotes.add(value);
                return { ...state, notes: newNotes, notesPendingCell: null };
            }
            const newBoard = [...board];
            newBoard[selectedCell] = value;
            const newNotes = notes.map((s, i) => {
                if (i === selectedCell)
                    return new Set();
                const peers = getPeerIndexes(selectedCell);
                if (peers.has(i)) {
                    const copy = new Set(s);
                    copy.delete(value);
                    return copy;
                }
                return s;
            });
            const won = isSolved(newBoard);
            return {
                ...state,
                board: newBoard,
                notes: newNotes,
                notesPendingCell: null,
                status: won ? "won" : "playing",
            };
        }
        case "ERASE": {
            const { selectedCell, puzzle, board } = state;
            if (selectedCell === null || puzzle === null)
                return state;
            if (puzzle.puzzle[selectedCell] !== 0)
                return state;
            const newBoard = [...board];
            newBoard[selectedCell] = 0;
            const newNotes = state.notes.map((s, i) => i === selectedCell ? new Set() : s);
            return { ...state, board: newBoard, notes: newNotes };
        }
        case "TOGGLE_NOTES_MODE": {
            return { ...state, notesMode: !state.notesMode };
        }
        case "SET_NOTES_PENDING_CELL": {
            return { ...state, notesPendingCell: action.index };
        }
        case "USE_HINT": {
            const { selectedCell, puzzle, board } = state;
            if (selectedCell === null || puzzle === null)
                return state;
            if (puzzle.puzzle[selectedCell] !== 0)
                return state;
            const solution = puzzle.solution[selectedCell];
            const newBoard = [...board];
            newBoard[selectedCell] = solution;
            const newNotes = state.notes.map((s, i) => {
                if (i === selectedCell)
                    return new Set();
                const peers = getPeerIndexes(selectedCell);
                if (peers.has(i)) {
                    const copy = new Set(s);
                    copy.delete(solution);
                    return copy;
                }
                return s;
            });
            const won = isSolved(newBoard);
            return {
                ...state,
                board: newBoard,
                notes: newNotes,
                hintsUsed: state.hintsUsed + 1,
                status: won ? "won" : "playing",
            };
        }
        case "TICK": {
            if (state.status !== "playing")
                return state;
            return { ...state, elapsedSeconds: state.elapsedSeconds + 1 };
        }
        case "PAUSE": {
            return { ...state, status: "paused" };
        }
        case "RESUME": {
            if (state.status !== "paused")
                return state;
            return { ...state, status: "playing" };
        }
        case "GO_TO_MENU": {
            return { ...initialState };
        }
        case "RESTORE_BOARD": {
            return {
                ...state,
                board: action.board,
                notes: action.notes,
                notesPendingCell: null,
                status: state.status === "won" ? "playing" : state.status,
            };
        }
        default:
            return state;
    }
}
export function useGame() {
    const [state, dispatch] = useReducer(gameReducer, initialState);
    // ---------------------------------------------------------------------------
    // Undo stack — lives outside the reducer so it isn't part of serialised state
    // ---------------------------------------------------------------------------
    const MAX_UNDO = 50;
    const undoStack = useRef([]);
    const [canUndo, setCanUndo] = useState(false);
    const pushSnapshot = useCallback((board, notes) => {
        undoStack.current = [
            ...undoStack.current.slice(-(MAX_UNDO - 1)),
            { board: [...board], notes: notes.map((s) => new Set(s)) },
        ];
        setCanUndo(true);
    }, []);
    const clearUndo = useCallback(() => {
        undoStack.current = [];
        setCanUndo(false);
    }, []);
    // performUndo as useCallback so the keyboard handler can depend on it
    const performUndo = useCallback(() => {
        const stack = undoStack.current;
        if (stack.length === 0)
            return;
        const snap = stack[stack.length - 1];
        undoStack.current = stack.slice(0, -1);
        setCanUndo(undoStack.current.length > 0);
        dispatch({ type: "RESTORE_BOARD", board: snap.board, notes: snap.notes });
    }, []);
    // ---------------------------------------------------------------------------
    // Timer
    // ---------------------------------------------------------------------------
    useEffect(() => {
        if (state.status !== "playing")
            return;
        const id = setInterval(() => dispatch({ type: "TICK" }), 1000);
        return () => clearInterval(id);
    }, [state.status]);
    // ---------------------------------------------------------------------------
    // Keyboard navigation — depends on performUndo so it's always fresh
    // ---------------------------------------------------------------------------
    useEffect(() => {
        const handler = (e) => {
            if (state.status !== "playing")
                return;
            const { selectedCell } = state;
            if (e.key === "Escape") {
                dispatch({ type: "SET_NOTES_PENDING_CELL", index: null });
                return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key === "z") {
                e.preventDefault();
                performUndo();
                return;
            }
            if (e.key >= "1" && e.key <= "9") {
                e.preventDefault();
                enterValue(Number(e.key));
                return;
            }
            if (e.key === "Backspace" || e.key === "Delete" || e.key === "0") {
                dispatch({ type: "ERASE" });
                return;
            }
            if (e.key === "n" || e.key === "N") {
                dispatch({ type: "TOGGLE_NOTES_MODE" });
                return;
            }
            if (selectedCell === null)
                return;
            const row = Math.floor(selectedCell / 9);
            const col = selectedCell % 9;
            let next = selectedCell;
            if (e.key === "ArrowUp" && row > 0)
                next = idx(row - 1, col);
            if (e.key === "ArrowDown" && row < 8)
                next = idx(row + 1, col);
            if (e.key === "ArrowLeft" && col > 0)
                next = idx(row, col - 1);
            if (e.key === "ArrowRight" && col < 8)
                next = idx(row, col + 1);
            if (next !== selectedCell) {
                e.preventDefault();
                dispatch({ type: "SELECT_CELL", index: next });
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [state.status, state.selectedCell, performUndo]);
    // ---------------------------------------------------------------------------
    // Derived highlight sets
    // ---------------------------------------------------------------------------
    const peerCells = state.selectedCell !== null
        ? getPeerIndexes(state.selectedCell)
        : new Set();
    const sameValueCells = new Set();
    if (state.selectedCell !== null) {
        const val = state.board[state.selectedCell];
        if (val !== 0) {
            state.board.forEach((v, i) => {
                if (v === val && i !== state.selectedCell)
                    sameValueCells.add(i);
            });
        }
    }
    // ---------------------------------------------------------------------------
    // Public API — enterValue/erase/useHint push snapshots before dispatching
    // ---------------------------------------------------------------------------
    const enterValue = useCallback((value) => {
        const { status, selectedCell, puzzle, board, notes, notesMode, notesPendingCell } = state;
        if (status !== "playing" || selectedCell === null || puzzle === null)
            return;
        if (puzzle.puzzle[selectedCell] !== 0)
            return;
        const effectiveNotesMode = notesPendingCell === selectedCell ? true : notesMode;
        pushSnapshot(board, notes); // always snapshot for undo (notes or value)
        dispatch({ type: "ENTER_VALUE", value });
    }, [state.status, state.selectedCell, state.puzzle, state.board, state.notes, state.notesMode, state.notesPendingCell, pushSnapshot]);
    const erase = useCallback(() => {
        const { selectedCell, puzzle, board, notes } = state;
        if (selectedCell === null || puzzle === null)
            return;
        if (puzzle.puzzle[selectedCell] !== 0)
            return;
        if (board[selectedCell] === 0 && notes[selectedCell].size === 0)
            return;
        pushSnapshot(board, notes);
        dispatch({ type: "ERASE" });
    }, [state, pushSnapshot]);
    const useHint = useCallback(() => {
        const { selectedCell, puzzle, board, notes } = state;
        if (selectedCell === null || puzzle === null)
            return;
        if (puzzle.puzzle[selectedCell] !== 0)
            return;
        pushSnapshot(board, notes);
        dispatch({ type: "USE_HINT" });
    }, [state, pushSnapshot]);
    return {
        state,
        startGame: (difficulty) => {
            clearUndo();
            dispatch({ type: "START_GAME", puzzle: generatePuzzle(difficulty) });
        },
        startDailyGame: (puzzle, date) => {
            clearUndo();
            dispatch({ type: "START_DAILY_GAME", puzzle, date });
        },
        selectCell: (index) => dispatch({ type: "SELECT_CELL", index }),
        enterValue,
        erase,
        toggleNotesMode: () => dispatch({ type: "TOGGLE_NOTES_MODE" }),
        setNotesPendingCell: (index) => dispatch({ type: "SET_NOTES_PENDING_CELL", index }),
        useHint,
        pause: () => dispatch({ type: "PAUSE" }),
        resume: () => dispatch({ type: "RESUME" }),
        goToMenu: () => {
            clearUndo();
            dispatch({ type: "GO_TO_MENU" });
        },
        undo: performUndo,
        canUndo,
        peerCells,
        sameValueCells,
    };
}
